// DealProtection.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { dataGridStyle, palette } from "../../theme";
import Header from "../../components/Header";
import { Panel, MUTED, SURFACE, SURFACE_HEAD, BORDER } from "../dashboard/dashboard-components/resellerUi";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import API from "../../api/index";
import {
  startToast,
  updateToastError,
  updateToastSuccess,
  updateToastWarning,
} from "../../utils/notification-loading";
import {
  Box,
  Grid,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Switch,
} from "@mui/material";

dayjs.extend(utc);
dayjs.extend(timezone);

const CORPORATE_TYPE = "CORPORATE";
const VIRTUAL_TYPE = "VIRTUAL";

const PLATFORM_MICRO = "PLATFORM MICRO";
const PLATFORM_SMALL = "PLATFORM SMALL";
const PLATFORM_MEDIUM = "PLATFORM MEDIUM";
const PLATFORM_LARGE = "PLATFORM LARGE";

const PLATFORM_EXT_SMALL = "PLATFORM EXT. SMALL";
const PLATFORM_EXT_MEDIUM = "PLATFORM EXT. MEDIUM";
const PLATFORM_EXT_LARGE = "PLATFORM EXT. LARGE";

const EXT_TYPES = [
  PLATFORM_EXT_SMALL,
  PLATFORM_EXT_MEDIUM,
  PLATFORM_EXT_LARGE,
];
const EXT_UNLOCK_TYPES = [
  PLATFORM_MICRO,
  PLATFORM_SMALL,
  PLATFORM_MEDIUM,
  PLATFORM_LARGE,
];

const INITIAL_ERRORS = {
  vat: "",
  name: "",
  company: "",
  location: "",
  address: "",
  email: "",
  licenses: "",

  selectedTypeLecs1: "",
  quantity1: "",
  selectedTypeLicenses1: "",
  hosts1: "",

  selectedTypeLecs2: "",
  quantity2: "",
  selectedTypeLicenses2: "",
  hosts2: "",

  selectedTypeLecs3: "",
  quantity3: "",
  selectedTypeLicenses3: "",
  hosts3: "",

  selectedTypeLecs4: "",
  quantity4: "",
  selectedTypeLicenses4: "",
  hosts4: "",

  selectedTypeLecs5: "",
  quantity5: "",
  selectedTypeLicenses5: "",
  hosts5: "",

  selectedTypeLecs6: "",
  quantity6: "",
  selectedTypeLicenses6: "",
  hosts6: "",

  business: "",
  core2p: "",
  core4p: "",
  coreSfp: "",
  extSmall: "",
  extMedium: "",
  extLarge: "",
  virtual: "",
  virtualHosts: "",
  corporateAmount: "",
  corporateHosts: "",

  editBusinessLicense: "",
  editCore2pLicense: "",
  editCore4pLicense: "",
  editCoreSfpLicense: "",
  editExtSmallLicense: "",
  editExtMediumLicense: "",
  editExtLargeLicense: "",
};

const EXPIRING_DAYS = 30;

const STATE_FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "expiring", label: "Expiring" },
  { key: "expired", label: "Expired" },
];

const isHostBasedType = (t) => t === CORPORATE_TYPE || t === VIRTUAL_TYPE;
const isExtType = (t) => EXT_TYPES.includes(t);

const sanitizePositiveIntegerInput = (value) => {
  const cleanValue = String(value || "").replace(/[^0-9]/g, "");

  if (!cleanValue) return "";

  const numberValue = parseInt(cleanValue, 10);

  if (!Number.isFinite(numberValue) || numberValue <= 0) {
    return "";
  }

  return String(numberValue);
};

// ISO dal server -> "DD/MM/YYYY HH:mm" nel timezone dell'utente; null se non valida.
const formatDate = (v) => {
  if (!v || /NaN/.test(String(v))) return null;
  const d = dayjs(v);
  if (!d.isValid()) return null;
  const tz = sessionStorage.getItem("timezone") || "Europe/Rome";
  return d.tz(tz).format("DD/MM/YYYY HH:mm");
};

const chipSx = (active) => ({
  color: active ? "#eaeaea" : MUTED,
  backgroundColor: active ? "rgba(69,144,149,0.18)" : "transparent",
  border: "1px solid",
  borderColor: active ? palette.primary.main : "rgba(255,255,255,0.14)",
  "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" },
});

// ---------------------------
// COMPONENTI DI PRESENTAZIONE
// ---------------------------

const SectionLabel = ({ children }) => (
  <Typography
    sx={{ mt: 2.5, mb: 1.25, fontSize: 12, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: MUTED }}
  >
    {children}
  </Typography>
);

const Field = ({ label, value, onChange, error, required = true, ...rest }) => (
  <TextField
    size="small"
    fullWidth
    required={required}
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    error={!!error}
    helperText={error}
    {...rest}
  />
);

const QtyField = ({ value, onChange, error, required, label = "Number of appliances" }) => (
  <Field
    label={label}
    value={value}
    onChange={(v) => onChange(sanitizePositiveIntegerInput(v))}
    error={error}
    required={required}
    slotProps={{ htmlInput: { maxLength: 4, inputMode: "numeric" } }}
  />
);

const HostsField = ({ value, onChange, error, required }) => (
  <Field
    label="Number hosts"
    value={value}
    onChange={(v) => onChange(sanitizePositiveIntegerInput(v))}
    error={error}
    required={required}
    slotProps={{ htmlInput: { maxLength: 6, inputMode: "numeric" } }}
  />
);

const ModalFrame = ({ open, onClose, title, subtitle, maxWidth = "md", actions, children }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth
    slotProps={{
      paper: {
        sx: { backgroundColor: SURFACE, backgroundImage: "none", border: `1px solid ${BORDER}`, borderRadius: "10px" },
      },
    }}
  >
    <DialogTitle
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.25,
        backgroundColor: SURFACE_HEAD,
        fontSize: 16,
        fontWeight: 700,
      }}
    >
      <Box component="span">
        {subtitle && (
          <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>
            {subtitle}
            {title ? " — " : ""}
          </Box>
        )}
        {title}
      </Box>
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>

    <DialogContent sx={{ pt: "20px !important" }}>{children}</DialogContent>

    {actions && <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${BORDER}` }}>{actions}</DialogActions>}
  </Dialog>
);

const VatField = ({ value, isItalian, error, onChange, onModeChange }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
    <Field
      label="VAT"
      value={value}
      onChange={onChange}
      error={error}
      slotProps={{ htmlInput: { maxLength: isItalian ? 11 : 28 } }}
    />
    <FormControlLabel
      label="IT"
      labelPlacement="start"
      sx={{ m: 0, mt: "2px", flexShrink: 0, userSelect: "none", "& .MuiTypography-root": { fontWeight: 600 } }}
      control={
        <Switch
          checked={isItalian}
          onChange={(e) => onModeChange(e.target.checked)}
          slotProps={{ input: { "aria-label": "Italian VAT" } }}
        />
      }
    />
  </Box>
);

// Anagrafica + business unit: stessi campi nel form di creazione e nel dialog di modifica
const CustomerFields = ({ f, set, errors }) => (
  <Grid container spacing={1.5}>
    <Grid size={12}>
      <VatField value={f.vat} isItalian={f.isItalianVat} error={errors.vat} onChange={set.vat} onModeChange={set.vatMode} />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <Field label="Contact name" value={f.name} onChange={set.name} error={errors.name} />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <Field label="Email" value={f.email} onChange={set.email} error={errors.email} />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <Field label="Company name" value={f.company} onChange={set.company} error={errors.company} />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <Field label="Location/Department" value={f.location} onChange={set.location} error={errors.location} />
    </Grid>
    <Grid size={{ xs: 12, sm: 8 }}>
      <Field label="Address" value={f.address} onChange={set.address} error={errors.address} />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <Field label="Years of licenses" value={f.licenses} onChange={set.licenses} error={errors.licenses} />
    </Grid>

    <Grid size={12}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: MUTED }}>
        Business unit
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2 }}>
        <RadioGroup row value={f.businessUnit} onChange={(e) => set.businessUnit(e.target.value)} name="business-unit">
          <FormControlLabel value="centrale" control={<Radio size="small" />} label="Headquarters" />
          <FormControlLabel value="altra" control={<Radio size="small" />} label="Other location" />
        </RadioGroup>

        <TextField
          placeholder="Location name"
          value={f.unitName}
          onChange={(e) => set.unitName(e.target.value)}
          size="small"
          disabled={f.businessUnit !== "altra"}
          slotProps={{ htmlInput: { "aria-label": "Location name" } }}
        />
      </Box>
    </Grid>
  </Grid>
);

// Blocco prodotto del form di creazione: tipo LECS + quantita + licenza (o host)
const ProductBlock = ({ n, cfg, errors, nextOpen, onToggle }) => {
  const hostBased = isHostBasedType(cfg.type);
  return (
    <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, borderRadius: "10px", backgroundColor: "rgba(255,255,255,0.02)" }}>
      <Typography sx={{ mb: 1.25, fontSize: 12, fontWeight: 600, color: MUTED }}>Product {n}</Typography>

      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            size="small"
            fullWidth
            required
            label="LECS type"
            value={cfg.type}
            error={!!errors[`selectedTypeLecs${n}`]}
            helperText={errors[`selectedTypeLecs${n}`]}
            onChange={(e) => cfg.setType(e.target.value)}
          >
            {cfg.options.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <QtyField value={cfg.qty} onChange={cfg.setQty} error={errors[`quantity${n}`]} required={!hostBased} />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          {hostBased ? (
            <HostsField value={cfg.hosts} onChange={cfg.setHosts} error={errors[`hosts${n}`]} required />
          ) : (
            <TextField
              select
              size="small"
              fullWidth
              required
              label="License type"
              value={cfg.lic}
              disabled={!cfg.type || isExtType(cfg.type)}
              error={!!errors[`selectedTypeLicenses${n}`]}
              helperText={errors[`selectedTypeLicenses${n}`]}
              onChange={(e) => cfg.setLic(e.target.value)}
            >
              {cfg.licTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Grid>

        {onToggle && (
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex", justifyContent: { sm: "flex-end" }, alignItems: "flex-start" }}>
            <Button
              size="small"
              color="inherit"
              startIcon={nextOpen ? <RemoveIcon /> : <AddIcon />}
              onClick={onToggle}
              sx={{ mt: "3px" }}
            >
              {nextOpen ? "Remove next products" : "Add another product"}
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const EditRow = ({ label, children }) => (
  <Grid container spacing={1.5} alignItems="flex-start">
    <Grid size={{ xs: 12, sm: 3.5 }} sx={{ pt: { sm: "8px" } }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{label}</Typography>
    </Grid>
    {children}
  </Grid>
);

const DealProtection = () => {
  // ---------------------------
  // FORM STATE
  // ---------------------------
  const [vat, setVat] = useState("");
  const [isItalianVat, setIsItalianVat] = useState(true);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [licenses, setLicenses] = useState("");
  const [rows, setRows] = useState([]);
  const [charged, setCharged] = useState(false);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  const [businessUnit, setBusinessUnit] = useState("centrale");
  const [unitName, setUnitName] = useState("");

  const [typesLecs] = useState([
    PLATFORM_MICRO,
    PLATFORM_SMALL,
    PLATFORM_MEDIUM,
    PLATFORM_LARGE,
    PLATFORM_EXT_SMALL,
    PLATFORM_EXT_MEDIUM,
    PLATFORM_EXT_LARGE,
    VIRTUAL_TYPE,
    CORPORATE_TYPE,
  ]);

  const [typesLecs2, setTypesLecs2] = useState([]);
  const [typesLecs3, setTypesLecs3] = useState([]);
  const [typesLecs4, setTypesLecs4] = useState([]);
  const [typesLecs5, setTypesLecs5] = useState([]);
  const [typesLecs6, setTypesLecs6] = useState([]);

  const [selectedTypeLecs1, setSelectedTypeLecs1] = useState("");
  const [quantity1, setQuantity1] = useState("");
  const [hosts1, setHosts1] = useState("");
  const [selectedTypeLicenses1, setSelectedTypeLicenses1] = useState("");

  const [selectedTypeLecs2, setSelectedTypeLecs2] = useState("");
  const [quantity2, setQuantity2] = useState("");
  const [hosts2, setHosts2] = useState("");
  const [selectedTypeLicenses2, setSelectedTypeLicenses2] = useState("");

  const [selectedTypeLecs3, setSelectedTypeLecs3] = useState("");
  const [quantity3, setQuantity3] = useState("");
  const [hosts3, setHosts3] = useState("");
  const [selectedTypeLicenses3, setSelectedTypeLicenses3] = useState("");

  const [selectedTypeLecs4, setSelectedTypeLecs4] = useState("");
  const [quantity4, setQuantity4] = useState("");
  const [hosts4, setHosts4] = useState("");
  const [selectedTypeLicenses4, setSelectedTypeLicenses4] = useState("");

  const [selectedTypeLecs5, setSelectedTypeLecs5] = useState("");
  const [quantity5, setQuantity5] = useState("");
  const [hosts5, setHosts5] = useState("");
  const [selectedTypeLicenses5, setSelectedTypeLicenses5] = useState("");

  const [selectedTypeLecs6, setSelectedTypeLecs6] = useState("");
  const [quantity6, setQuantity6] = useState("");
  const [hosts6, setHosts6] = useState("");
  const [selectedTypeLicenses6, setSelectedTypeLicenses6] = useState("");

  const [visibility2, setVisibility2] = useState(false);
  const [visibility3, setVisibility3] = useState(false);
  const [visibility4, setVisibility4] = useState(false);
  const [visibility5, setVisibility5] = useState(false);
  const [visibility6, setVisibility6] = useState(false);

  // Dialog edit
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Quantità in edit (dialog)
  const [numBusiness, setNumBusiness] = useState(0);
  const [numCore2P, setNumCore2P] = useState(0);
  const [numCore4P, setNumCore4P] = useState(0);
  const [numCoreSfp, setNumCoreSfp] = useState(0);

  const [numExtSmall, setNumExtSmall] = useState(0);
  const [numExtMedium, setNumExtMedium] = useState(0);
  const [numExtLarge, setNumExtLarge] = useState(0);

  const [numVirtual, setNumVirtual] = useState(0);
  const [numVirtualHosts, setNumVirtualHosts] = useState(0);

  const [numCorporateAmount, setNumCorporateAmount] = useState(0);
  const [numCorporateHosts, setNumCorporateHosts] = useState(0);

  // Licenze per tipo (select) - CREATE
  const [typesLicenses1, setTypesLicenses1] = useState([]);
  const [typesLicenses2, setTypesLicenses2] = useState([]);
  const [typesLicenses3, setTypesLicenses3] = useState([]);
  const [typesLicenses4, setTypesLicenses4] = useState([]);
  const [typesLicenses5, setTypesLicenses5] = useState([]);
  const [typesLicenses6, setTypesLicenses6] = useState([]);

  // Licenze edit
  const [editBusinessLicense, setEditBusinessLicense] = useState("-");
  const [editCore2pLicense, setEditCore2pLicense] = useState("-");
  const [editCore4pLicense, setEditCore4pLicense] = useState("-");
  const [editCoreSfpLicense, setEditCoreSfpLicense] = useState("-");
  const [editExtSmallLicense, setEditExtSmallLicense] = useState("-");
  const [editExtMediumLicense, setEditExtMediumLicense] = useState("-");
  const [editExtLargeLicense, setEditExtLargeLicense] = useState("-");

  const [businessTypeLicenses] = useState(["Lecs Microb PLUS"]);
  const [core2pTypeLicenses] = useState([
    "Lecs Platform Small - PLUS",
    "Lecs Platform Small - PRO",
    "Lecs Platform Small - STD",
  ]);
  const [core4pTypeLicenses] = useState([
    "Lecs Platform Medium - PLUS",
    "Lecs Platform Medium - PRO",
    "Lecs Platform Medium - STD",
  ]);
  const [coreSfpTypeLicenses] = useState([
    "Lecs Platform Large - STD",
    "Lecs Platform Large - PLUS",
  ]);

  const [extSmallTypeLicenses] = useState(["Lecs Platform Ext. Small"]);
  const [extMediumTypeLicenses] = useState(["Lecs Platform Ext. Medium"]);
  const [extLargeTypeLicenses] = useState(["Lecs Platform Ext. Large"]);

  const [virtualTypeLicenses] = useState(["Lecs VM Proxmax", "Lecs VM Vmware"]);

  // ---------------------------
  // HELPERS
  // ---------------------------

  const getFixedExtLicense = (type) => {
    if (type === PLATFORM_EXT_SMALL) return "Lecs Platform Ext. Small";
    if (type === PLATFORM_EXT_MEDIUM) return "Lecs Platform Ext. Medium";
    if (type === PLATFORM_EXT_LARGE) return "Lecs Platform Ext. Large";
    return "";
  };

  const getDefaultLicenseValue = (type) => {
    if (isHostBasedType(type)) return "-";
    if (isExtType(type)) return getFixedExtLicense(type);
    return "";
  };

  const resetError = () => {
    setErrors(INITIAL_ERRORS);
  };

  const sanitizeNumericInput = (value) => value.replace(/[^0-9]/g, "");

  const toNumber = (value) => parseInt(String(value || "0"), 10) || 0;

  const isPositiveNumber = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0;
  };

  const sanitizeVatInput = (value, italianMode) => {
    if (italianMode) {
      return value.replace(/[^0-9]/g, "").slice(0, 11);
    }
    return value.slice(0, 28);
  };

  const handleVatChange = (value) => {
    setVat(sanitizeVatInput(value, isItalianVat));
  };

  const handleVatModeChange = (checked) => {
    setIsItalianVat(checked);
    setVat((prev) => sanitizeVatInput(prev, checked));
  };

  const resetForm = () => {
    setVat("");
    setIsItalianVat(true);
    setName("");
    setCompany("");
    setLocation("");
    setAddress("");
    setLicenses("");
    setEmail("");
    setBusinessUnit("centrale");
    setUnitName("");

    setSelectedTypeLecs1("");
    setQuantity1("");
    setHosts1("");
    setSelectedTypeLicenses1("");

    setSelectedTypeLecs2("");
    setQuantity2("");
    setHosts2("");
    setSelectedTypeLicenses2("");

    setSelectedTypeLecs3("");
    setQuantity3("");
    setHosts3("");
    setSelectedTypeLicenses3("");

    setSelectedTypeLecs4("");
    setQuantity4("");
    setHosts4("");
    setSelectedTypeLicenses4("");

    setSelectedTypeLecs5("");
    setQuantity5("");
    setHosts5("");
    setSelectedTypeLicenses5("");

    setSelectedTypeLecs6("");
    setQuantity6("");
    setHosts6("");
    setSelectedTypeLicenses6("");

    setVisibility2(false);
    setVisibility3(false);
    setVisibility4(false);
    setVisibility5(false);
    setVisibility6(false);

    resetError();
  };

  const getAccountItemIdByVatSafe = async (vatString) => {
    const vatClean = String(vatString ?? "").trim();
    if (!vatClean) return null;

    try {
      const resp = await API.getAccountMonday({ vat: vatClean });
      const itemId =
        resp?.data?.itemId ??
        resp?.data?.itemID ??
        resp?.data?.item_id ??
        null;
      const n = Number(itemId);
      return Number.isFinite(n) ? n : null;
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 404) {
        
      }
      return null;
    }
  };

  const setTypeLicenses = (selectedLecs) => {
    if (selectedLecs === PLATFORM_MICRO) return businessTypeLicenses;
    if (selectedLecs === PLATFORM_SMALL) return core2pTypeLicenses;
    if (selectedLecs === PLATFORM_MEDIUM) return core4pTypeLicenses;
    if (selectedLecs === PLATFORM_LARGE) return coreSfpTypeLicenses;
    if (selectedLecs === PLATFORM_EXT_SMALL) return extSmallTypeLicenses;
    if (selectedLecs === PLATFORM_EXT_MEDIUM) return extMediumTypeLicenses;
    if (selectedLecs === PLATFORM_EXT_LARGE) return extLargeTypeLicenses;
    if (selectedLecs === VIRTUAL_TYPE) return virtualTypeLicenses;
    return [];
  };

  const getSelectedTypesUpToBlock = (blockIdx) => {
    const map = [
      selectedTypeLecs1,
      selectedTypeLecs2,
      selectedTypeLecs3,
      selectedTypeLecs4,
      selectedTypeLecs5,
      selectedTypeLecs6,
    ];
    return map.slice(0, blockIdx - 1).filter(Boolean);
  };

  const canUnlockExtWithPreviousSelections = (previousSelected = []) => {
    return previousSelected.some((type) => EXT_UNLOCK_TYPES.includes(type));
  };

  const getAvailableTypes = (allTypes, previousSelected = []) => {
    const extUnlocked = canUnlockExtWithPreviousSelections(previousSelected);

    return allTypes.filter((type) => {
      if (previousSelected.includes(type)) return false;
      if (EXT_TYPES.includes(type) && !extUnlocked) return false;
      return true;
    });
  };

  const fetchProtections = useCallback(() => {
    API.getProtection()
      .then((result) => setRows(Array.isArray(result.data) ? result.data : []))
      .catch(() => {})
      .finally(() => setCharged(true));
  }, []);

  // ---------------------------
  // EFFECTS
  // ---------------------------
  useEffect(() => {
    fetchProtections();
  }, [fetchProtections]);

  useEffect(() => {
    setTypesLecs2(getAvailableTypes(typesLecs, [selectedTypeLecs1].filter(Boolean)));
    setTypesLicenses1(setTypeLicenses(selectedTypeLecs1));
    setSelectedTypeLicenses1(getDefaultLicenseValue(selectedTypeLecs1));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAvailableTypes/getDefaultLicenseValue/setTypeLicenses are plain functions recreated every render; adding them would re-run this effect on every render
  }, [selectedTypeLecs1, typesLecs]);

  useEffect(() => {
    setTypesLecs3(
      getAvailableTypes(typesLecs, [selectedTypeLecs1, selectedTypeLecs2].filter(Boolean))
    );
    setTypesLicenses2(setTypeLicenses(selectedTypeLecs2));
    setSelectedTypeLicenses2(getDefaultLicenseValue(selectedTypeLecs2));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAvailableTypes/getDefaultLicenseValue/setTypeLicenses are plain functions recreated every render; adding them would re-run this effect on every render
  }, [selectedTypeLecs1, selectedTypeLecs2, typesLecs]);

  useEffect(() => {
    setTypesLecs4(
      getAvailableTypes(
        typesLecs,
        [selectedTypeLecs1, selectedTypeLecs2, selectedTypeLecs3].filter(Boolean)
      )
    );
    setTypesLicenses3(setTypeLicenses(selectedTypeLecs3));
    setSelectedTypeLicenses3(getDefaultLicenseValue(selectedTypeLecs3));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAvailableTypes/getDefaultLicenseValue/setTypeLicenses are plain functions recreated every render; adding them would re-run this effect on every render
  }, [selectedTypeLecs1, selectedTypeLecs2, selectedTypeLecs3, typesLecs]);

  useEffect(() => {
    setTypesLecs5(
      getAvailableTypes(
        typesLecs,
        [
          selectedTypeLecs1,
          selectedTypeLecs2,
          selectedTypeLecs3,
          selectedTypeLecs4,
        ].filter(Boolean)
      )
    );
    setTypesLicenses4(setTypeLicenses(selectedTypeLecs4));
    setSelectedTypeLicenses4(getDefaultLicenseValue(selectedTypeLecs4));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAvailableTypes/getDefaultLicenseValue/setTypeLicenses are plain functions recreated every render; adding them would re-run this effect on every render
  }, [
    selectedTypeLecs1,
    selectedTypeLecs2,
    selectedTypeLecs3,
    selectedTypeLecs4,
    typesLecs,
  ]);

  useEffect(() => {
    setTypesLecs6(
      getAvailableTypes(
        typesLecs,
        [
          selectedTypeLecs1,
          selectedTypeLecs2,
          selectedTypeLecs3,
          selectedTypeLecs4,
          selectedTypeLecs5,
        ].filter(Boolean)
      )
    );
    setTypesLicenses5(setTypeLicenses(selectedTypeLecs5));
    setSelectedTypeLicenses5(getDefaultLicenseValue(selectedTypeLecs5));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAvailableTypes/getDefaultLicenseValue/setTypeLicenses are plain functions recreated every render; adding them would re-run this effect on every render
  }, [
    selectedTypeLecs1,
    selectedTypeLecs2,
    selectedTypeLecs3,
    selectedTypeLecs4,
    selectedTypeLecs5,
    typesLecs,
  ]);

  useEffect(() => {
    setTypesLicenses6(setTypeLicenses(selectedTypeLecs6));
    setSelectedTypeLicenses6(getDefaultLicenseValue(selectedTypeLecs6));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getDefaultLicenseValue/setTypeLicenses are plain functions recreated every render; adding them would re-run this effect on every render
  }, [selectedTypeLecs6]);

  useEffect(() => {
    if (!selectedRow) return;

    const vatValue = String(selectedRow.vat ?? "").trim();

    setVat(vatValue);
    setIsItalianVat(/^[0-9]{11}$/.test(vatValue));
    setName(selectedRow.name ?? "");
    setCompany(selectedRow.companyName ?? "");
    setLocation(selectedRow.location ?? "");
    setAddress(selectedRow.address ?? "");
    setBusinessUnit(selectedRow.businessUnit ?? "centrale");
    setUnitName(selectedRow.otherLocation ?? "");
    setEmail(selectedRow.email ?? "");
    setLicenses(String(selectedRow.licenses ?? ""));

    setNumBusiness(selectedRow.business ?? 0);
    setNumCore2P(selectedRow.core2p ?? 0);
    setNumCore4P(selectedRow.core4p ?? 0);
    setNumCoreSfp(selectedRow.coreSfp ?? 0);

    setNumExtSmall(selectedRow.extSmall ?? 0);
    setNumExtMedium(selectedRow.extMedium ?? 0);
    setNumExtLarge(selectedRow.extLarge ?? 0);

    setNumVirtual(selectedRow.virtual ?? 0);
    setNumVirtualHosts(selectedRow.virtualHosts ?? 0);

    setNumCorporateAmount(selectedRow.corporateAmount ?? 0);
    setNumCorporateHosts(selectedRow.corporateHosts ?? selectedRow.corporate ?? 0);

    setEditBusinessLicense(selectedRow.businessLicense ?? "-");
    setEditCore2pLicense(selectedRow.core2pLicense ?? "-");
    setEditCore4pLicense(selectedRow.core4pLicense ?? "-");
    setEditCoreSfpLicense(selectedRow.coreSfpLicense ?? "-");

    setEditExtSmallLicense(
      selectedRow.extSmallLicense ??
        ((selectedRow.extSmall ?? 0) > 0 ? "Lecs Platform Ext. Small" : "-")
    );
    setEditExtMediumLicense(
      selectedRow.extMediumLicense ??
        ((selectedRow.extMedium ?? 0) > 0 ? "Lecs Platform Ext. Medium" : "-")
    );
    setEditExtLargeLicense(
      selectedRow.extLargeLicense ??
        ((selectedRow.extLarge ?? 0) > 0 ? "Lecs Platform Ext. Large" : "-")
    );
  }, [selectedRow]);

  useEffect(() => {
    if (openDialog) {
      resetError();
      return;
    }

    setSelectedRow(null);

    setNumBusiness(0);
    setNumCore2P(0);
    setNumCore4P(0);
    setNumCoreSfp(0);

    setNumExtSmall(0);
    setNumExtMedium(0);
    setNumExtLarge(0);

    setNumVirtual(0);
    setNumVirtualHosts(0);
    setNumCorporateAmount(0);
    setNumCorporateHosts(0);

    setEditBusinessLicense("-");
    setEditCore2pLicense("-");
    setEditCore4pLicense("-");
    setEditCoreSfpLicense("-");
    setEditExtSmallLicense("-");
    setEditExtMediumLicense("-");
    setEditExtLargeLicense("-");

    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetForm is a plain function recreated every render; adding it would re-run this effect on every render
  }, [openDialog]);

  // ---------------------------
  // BUSINESS LOGIC
  // ---------------------------
  const TypesLicenses = () => {
    const selectedTypes = [
      { typeLecs: selectedTypeLecs1, typeLicense: selectedTypeLicenses1 },
      { typeLecs: selectedTypeLecs2, typeLicense: selectedTypeLicenses2 },
      { typeLecs: selectedTypeLecs3, typeLicense: selectedTypeLicenses3 },
      { typeLecs: selectedTypeLecs4, typeLicense: selectedTypeLicenses4 },
      { typeLecs: selectedTypeLecs5, typeLicense: selectedTypeLicenses5 },
      { typeLecs: selectedTypeLecs6, typeLicense: selectedTypeLicenses6 },
    ];

    let business = "-";
    let core2p = "-";
    let core4p = "-";
    let coreSfp = "-";
    let extSmall = "-";
    let extMedium = "-";
    let extLarge = "-";
    let virtual = "-";

    for (const { typeLecs, typeLicense } of selectedTypes) {
      if (typeLecs === PLATFORM_MICRO) business = typeLicense;
      else if (typeLecs === PLATFORM_SMALL) core2p = typeLicense;
      else if (typeLecs === PLATFORM_MEDIUM) core4p = typeLicense;
      else if (typeLecs === PLATFORM_LARGE) coreSfp = typeLicense;
      else if (typeLecs === PLATFORM_EXT_SMALL) extSmall = typeLicense;
      else if (typeLecs === PLATFORM_EXT_MEDIUM) extMedium = typeLicense;
      else if (typeLecs === PLATFORM_EXT_LARGE) extLarge = typeLicense;
      else if (typeLecs === VIRTUAL_TYPE) virtual = typeLicense;
    }

    return [business, core2p, core4p, coreSfp, extSmall, extMedium, extLarge, virtual];
  };

  const QtyLecs = () => {
    const selectedTypes = [
      { type: selectedTypeLecs1, qty: quantity1 },
      { type: selectedTypeLecs2, qty: quantity2 },
      { type: selectedTypeLecs3, qty: quantity3 },
      { type: selectedTypeLecs4, qty: quantity4 },
      { type: selectedTypeLecs5, qty: quantity5 },
      { type: selectedTypeLecs6, qty: quantity6 },
    ];

    let business = 0;
    let core2p = 0;
    let core4p = 0;
    let coreSfp = 0;
    let extSmall = 0;
    let extMedium = 0;
    let extLarge = 0;
    let virtual = 0;

    for (const { type, qty } of selectedTypes) {
      const q = toNumber(qty);

      if (type === PLATFORM_MICRO) business = q;
      else if (type === PLATFORM_SMALL) core2p = q;
      else if (type === PLATFORM_MEDIUM) core4p = q;
      else if (type === PLATFORM_LARGE) coreSfp = q;
      else if (type === PLATFORM_EXT_SMALL) extSmall = q;
      else if (type === PLATFORM_EXT_MEDIUM) extMedium = q;
      else if (type === PLATFORM_EXT_LARGE) extLarge = q;
      else if (type === VIRTUAL_TYPE) virtual = q;
    }

    return [business, core2p, core4p, coreSfp, extSmall, extMedium, extLarge, virtual];
  };

  const HostBasedFromBlocks = () => {
    const blocks = [
      { type: selectedTypeLecs1, hosts: hosts1, amount: quantity1 },
      { type: selectedTypeLecs2, hosts: hosts2, amount: quantity2 },
      { type: selectedTypeLecs3, hosts: hosts3, amount: quantity3 },
      { type: selectedTypeLecs4, hosts: hosts4, amount: quantity4 },
      { type: selectedTypeLecs5, hosts: hosts5, amount: quantity5 },
      { type: selectedTypeLecs6, hosts: hosts6, amount: quantity6 },
    ];

    const foundCorporate = blocks.find((b) => b.type === CORPORATE_TYPE);
    const foundVirtual = blocks.find((b) => b.type === VIRTUAL_TYPE);

    const corporateHosts = toNumber(foundCorporate?.hosts);
    const corporateAmount = toNumber(foundCorporate?.amount);
    const virtualHosts = toNumber(foundVirtual?.hosts);

    return {
      corporateHosts,
      corporateAmount,
      virtualHosts,
    };
  };

  const validateBlockCreate = (idx, visible) => {
    if (!visible) return {};

    const dataByBlock = {
      1: {
        type: selectedTypeLecs1,
        qty: quantity1,
        lic: selectedTypeLicenses1,
        hosts: hosts1,
        eType: "selectedTypeLecs1",
        eQty: "quantity1",
        eLic: "selectedTypeLicenses1",
        eHosts: "hosts1",
      },
      2: {
        type: selectedTypeLecs2,
        qty: quantity2,
        lic: selectedTypeLicenses2,
        hosts: hosts2,
        eType: "selectedTypeLecs2",
        eQty: "quantity2",
        eLic: "selectedTypeLicenses2",
        eHosts: "hosts2",
      },
      3: {
        type: selectedTypeLecs3,
        qty: quantity3,
        lic: selectedTypeLicenses3,
        hosts: hosts3,
        eType: "selectedTypeLecs3",
        eQty: "quantity3",
        eLic: "selectedTypeLicenses3",
        eHosts: "hosts3",
      },
      4: {
        type: selectedTypeLecs4,
        qty: quantity4,
        lic: selectedTypeLicenses4,
        hosts: hosts4,
        eType: "selectedTypeLecs4",
        eQty: "quantity4",
        eLic: "selectedTypeLicenses4",
        eHosts: "hosts4",
      },
      5: {
        type: selectedTypeLecs5,
        qty: quantity5,
        lic: selectedTypeLicenses5,
        hosts: hosts5,
        eType: "selectedTypeLecs5",
        eQty: "quantity5",
        eLic: "selectedTypeLicenses5",
        eHosts: "hosts5",
      },
      6: {
        type: selectedTypeLecs6,
        qty: quantity6,
        lic: selectedTypeLicenses6,
        hosts: hosts6,
        eType: "selectedTypeLecs6",
        eQty: "quantity6",
        eLic: "selectedTypeLicenses6",
        eHosts: "hosts6",
      },
    };

    const current = dataByBlock[idx];
    const errs = {};

    if (!current?.type) {
      errs[current.eType] = "Lecs Type is required";
      return errs;
    }

    if (isExtType(current.type)) {
      const previousSelections = getSelectedTypesUpToBlock(idx);
      const canUnlock = canUnlockExtWithPreviousSelections(previousSelections);

      if (!canUnlock) {
        errs[current.eType] =
          "This EXT type can be selected only after MICRO, SMALL, MEDIUM or LARGE";
        return errs;
      }
    }

    if (isHostBasedType(current.type)) {
      if (!String(current.hosts ?? "").trim()) {
        errs[current.eHosts] = "Number hosts is required";
      } else if (!isPositiveNumber(current.hosts)) {
        errs[current.eHosts] = "Number hosts must be greater than 0";
      }
    } else {
      if (!String(current.qty ?? "").trim()) {
        errs[current.eQty] = "Number of appliances is required";
      } else if (!isPositiveNumber(current.qty)) {
        errs[current.eQty] = "Number of appliances must be greater than 0";
      }

      if (!String(current.lic ?? "").trim()) {
        errs[current.eLic] = "Type license is required";
      }
    }

    return errs;
  };

  const validate = () => {
    const newErrors = {};
    const trimmedVat = String(vat || "").trim();

    if (!trimmedVat) {
      newErrors.vat = "VAT is required";
    } else if (isItalianVat) {
      if (!/^[0-9]{11}$/.test(trimmedVat)) {
        newErrors.vat = "Italian VAT must contain exactly 11 digits";
      }
    } else if (trimmedVat.length > 28) {
      newErrors.vat = "VAT cannot exceed 28 characters";
    }

    if (!name.trim()) newErrors.name = "The name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(email.trim())) newErrors.email = "Email is not valid";
    if (!company.trim()) newErrors.company = "The company name is required";
    if (!location.trim()) newErrors.location = "The location is required";
    if (!address.trim()) newErrors.address = "The address is required";
    if (!licenses) newErrors.licenses = "Years of licensing are required";

    if (!openDialog) {
      // ---------------------------
      // VALIDAZIONE CREATE
      // ---------------------------
      Object.assign(newErrors, validateBlockCreate(1, true));
      Object.assign(newErrors, validateBlockCreate(2, visibility2));
      Object.assign(newErrors, validateBlockCreate(3, visibility2 && visibility3));
      Object.assign(newErrors, validateBlockCreate(4, visibility2 && visibility3 && visibility4));
      Object.assign(newErrors, validateBlockCreate(5, visibility2 && visibility3 && visibility4 && visibility5));
      Object.assign(
        newErrors,
        validateBlockCreate(6, visibility2 && visibility3 && visibility4 && visibility5 && visibility6)
      );
    } else {
      // ---------------------------
      // VALIDAZIONE EDIT (dialog)
      // ---------------------------

      // Regola generale per MICRO/SMALL/MEDIUM/LARGE/EXT*:
      // - se la licenza è diversa da "-" → la quantità deve essere > 0
      // - se la quantità è > 0 → la licenza deve essere diversa da "-"
      // - se licenza è "-" e quantità è 0 → ok (campo non utilizzato)

      const hasLicenseBusiness = String(editBusinessLicense).trim() !== "-";
      const hasLicenseCore2p = String(editCore2pLicense).trim() !== "-";
      const hasLicenseCore4p = String(editCore4pLicense).trim() !== "-";
      const hasLicenseCoreSfp = String(editCoreSfpLicense).trim() !== "-";
      const hasLicenseExtSmall = String(editExtSmallLicense).trim() !== "-";
      const hasLicenseExtMedium = String(editExtMediumLicense).trim() !== "-";
      const hasLicenseExtLarge = String(editExtLargeLicense).trim() !== "-";

      // PLATFORM MICRO
      if (hasLicenseBusiness && !isPositiveNumber(numBusiness)) {
        newErrors.business = "Number of appliances must be greater than 0 when a license is selected";
      } else if (!hasLicenseBusiness && isPositiveNumber(numBusiness)) {
        newErrors.editBusinessLicense = "Type license is required";
      }

      // PLATFORM SMALL
      if (hasLicenseCore2p && !isPositiveNumber(numCore2P)) {
        newErrors.core2p = "Number of appliances must be greater than 0 when a license is selected";
      } else if (!hasLicenseCore2p && isPositiveNumber(numCore2P)) {
        newErrors.editCore2pLicense = "Type license is required";
      }

      // PLATFORM MEDIUM
      if (hasLicenseCore4p && !isPositiveNumber(numCore4P)) {
        newErrors.core4p = "Number of appliances must be greater than 0 when a license is selected";
      } else if (!hasLicenseCore4p && isPositiveNumber(numCore4P)) {
        newErrors.editCore4pLicense = "Type license is required";
      }

      // PLATFORM LARGE
      if (hasLicenseCoreSfp && !isPositiveNumber(numCoreSfp)) {
        newErrors.coreSfp = "Number of appliances must be greater than 0 when a license is selected";
      } else if (!hasLicenseCoreSfp && isPositiveNumber(numCoreSfp)) {
        newErrors.editCoreSfpLicense = "Type license is required";
      }

      // PLATFORM EXT. SMALL
      if (hasLicenseExtSmall && !isPositiveNumber(numExtSmall)) {
        newErrors.extSmall = "Number of appliances must be greater than 0 when a license is selected";
      } else if (!hasLicenseExtSmall && isPositiveNumber(numExtSmall)) {
        newErrors.editExtSmallLicense = "Type license is required";
      }

      // PLATFORM EXT. MEDIUM
      if (hasLicenseExtMedium && !isPositiveNumber(numExtMedium)) {
        newErrors.extMedium = "Number of appliances must be greater than 0 when a license is selected";
      } else if (!hasLicenseExtMedium && isPositiveNumber(numExtMedium)) {
        newErrors.editExtMediumLicense = "Type license is required";
      }

      // PLATFORM EXT. LARGE
      if (hasLicenseExtLarge && !isPositiveNumber(numExtLarge)) {
        newErrors.extLarge = "Number of appliances must be greater than 0 when a license is selected";
      } else if (!hasLicenseExtLarge && isPositiveNumber(numExtLarge)) {
        newErrors.editExtLargeLicense = "Type license is required";
      }

      // VIRTUAL:
      // - se hosts > 0 → la quantità deve essere > 0
      // - se quantità > 0 → gli hosts devono essere > 0
      // - se entrambi 0 → ok (campo non utilizzato)
      if (toNumber(numVirtualHosts) > 0 && !isPositiveNumber(numVirtual)) {
        newErrors.virtual = "Number of appliances must be greater than 0 when hosts are set";
      } else if (isPositiveNumber(numVirtual) && !isPositiveNumber(numVirtualHosts)) {
        newErrors.virtualHosts = "Number hosts is required";
      }

      // CORPORATE:
      // - se hosts > 0 → la quantità deve essere > 0
      // - se quantità > 0 → gli hosts devono essere > 0
      // - se entrambi 0 → ok (campo non utilizzato)
      if (toNumber(numCorporateHosts) > 0 && !isPositiveNumber(numCorporateAmount)) {
        newErrors.corporateAmount = "Number of appliances must be greater than 0 when hosts are set";
      } else if (isPositiveNumber(numCorporateAmount) && !isPositiveNumber(numCorporateHosts)) {
        newErrors.corporateHosts = "Number hosts is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchDetails = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  // ---------------------------
  // SUBMIT: CREATE
  // ---------------------------
  const handleSubmit = () => {
    if (!validate()) return;

    const [qBusiness, qCore2p, qCore4p, qCoreSfp, qExtSmall, qExtMedium, qExtLarge, qVirtual] = QtyLecs();
    const [lBusiness, lCore2p, lCore4p, lCoreSfp, lExtSmall, lExtMedium, lExtLarge, lVirtual] =
      TypesLicenses();
    const { corporateHosts, corporateAmount, virtualHosts } = HostBasedFromBlocks();

    const payload = {
      vat,
      location,
      name,
      email,
      companyName: company,
      address,
      licenses: toNumber(licenses),
      businessUnit,

      business: toNumber(qBusiness),
      core2p: toNumber(qCore2p),
      core4p: toNumber(qCore4p),
      coreSfp: toNumber(qCoreSfp),

      extSmall: toNumber(qExtSmall),
      extMedium: toNumber(qExtMedium),
      extLarge: toNumber(qExtLarge),

      virtual: toNumber(qVirtual),

      businessLicense: lBusiness,
      core2pLicense: lCore2p,
      core4pLicense: lCore4p,
      coreSfpLicense: lCoreSfp,

      extSmallLicense: lExtSmall,
      extMediumLicense: lExtMedium,
      extLargeLicense: lExtLarge,

      virtualLicense: lVirtual,

      virtualHosts,
      corporateHosts,
      corporateAmount,
    };

    if (businessUnit === "altra") payload.otherLocation = unitName;

    startToast();
    API.setProtection(payload)
      .then(async (response) => {
        const snapshot = {
          vat,
          name,
          email,
          companyName: company,
          location,
          address,
          licenses,
          otherLocation: businessUnit === "altra" ? unitName : "",

          businessLicense: lBusiness,
          business: toNumber(qBusiness),

          core2pLicense: lCore2p,
          core2p: toNumber(qCore2p),

          core4pLicense: lCore4p,
          core4p: toNumber(qCore4p),

          coreSfpLicense: lCoreSfp,
          coreSfp: toNumber(qCoreSfp),

          extSmallLicense: lExtSmall,
          extSmall: toNumber(qExtSmall),

          extMediumLicense: lExtMedium,
          extMedium: toNumber(qExtMedium),

          extLargeLicense: lExtLarge,
          extLarge: toNumber(qExtLarge),

          virtualLicense: lVirtual,
          virtual: toNumber(qVirtual),
          virtualHosts,

          corporateHosts,
          corporateAmount,
        };

        try {
          const reseller = response?.data?.resellerInfo?.ragioneSociale ?? "";
          if (reseller) {
            const accountItemId = await getAccountItemIdByVatSafe(vat);
            await API.mondayCreateDealProtectionItem({
              reseller,
              formData: {
                ...snapshot,
                accountItemId,
              },
            });
          }
        } catch (e) {
          
        }

        fetchProtections();
        resetForm();

        updateToastSuccess("Deal Protection added");
      })
      .catch((error) => {
        updateToastError("Existing customer ", error);
      });
  };

  // ---------------------------
  // SUBMIT: DELETE
  // ---------------------------
  const deleteSubmit = () => {
    setOpenDialog(false);

    startToast();

    API.deleteCustomer({ id: selectedRow._id })
      .then(async () => {
        fetchProtections();
        updateToastSuccess("Customer deleted");
      })
      .catch((err) => {
        updateToastError("Error!");
        
      });
  };

  // ---------------------------
  // SUBMIT: UPDATE
  // ---------------------------
  const updateSubmit = () => {
    if (!validate()) return;

    startToast();

    const payload = {
      vat,
      location,
      name,
      email,
      companyName: company,
      address,
      licenses: toNumber(licenses),
      businessUnit,

      business: toNumber(numBusiness),
      core2p: toNumber(numCore2P),
      core4p: toNumber(numCore4P),
      coreSfp: toNumber(numCoreSfp),

      extSmall: toNumber(numExtSmall),
      extMedium: toNumber(numExtMedium),
      extLarge: toNumber(numExtLarge),

      virtual: toNumber(numVirtual),

      businessLicense: toNumber(numBusiness) === 0 ? "-" : editBusinessLicense,
      core2pLicense: toNumber(numCore2P) === 0 ? "-" : editCore2pLicense,
      core4pLicense: toNumber(numCore4P) === 0 ? "-" : editCore4pLicense,
      coreSfpLicense: toNumber(numCoreSfp) === 0 ? "-" : editCoreSfpLicense,

      extSmallLicense: toNumber(numExtSmall) === 0 ? "-" : "Lecs Platform Ext. Small",
      extMediumLicense: toNumber(numExtMedium) === 0 ? "-" : "Lecs Platform Ext. Medium",
      extLargeLicense: toNumber(numExtLarge) === 0 ? "-" : "Lecs Platform Ext. Large",

      virtualLicense: toNumber(numVirtual) === 0 ? "-" : virtualTypeLicenses[0],

      virtualHosts: toNumber(numVirtualHosts),
      corporateAmount: toNumber(numCorporateAmount),
      corporateHosts: toNumber(numCorporateHosts),
    };

    payload.otherLocation = businessUnit === "altra" ? unitName : "";

    const allZero =
      toNumber(numBusiness) === 0 &&
      toNumber(numCore2P) === 0 &&
      toNumber(numCore4P) === 0 &&
      toNumber(numCoreSfp) === 0 &&
      toNumber(numExtSmall) === 0 &&
      toNumber(numExtMedium) === 0 &&
      toNumber(numExtLarge) === 0 &&
      toNumber(numVirtual) === 0 &&
      toNumber(numCorporateAmount) === 0;

    if (allZero) {
      updateToastError("Select at least one quantity");
      return;
    }

    setOpenDialog(false);

    API.updateCustomer({ id: selectedRow._id, payload })
      .then(async () => {
        fetchProtections();

        let mondaySyncFailed = false;
        try {
          const vatKey = selectedRow?.vat ?? vat;
          if (vatKey) {
            const accountItemId = await getAccountItemIdByVatSafe(vat);

            const formData = {
              vat,
              name,
              email,
              companyName: company,
              location,
              address,
              licenses,
              otherLocation: businessUnit === "altra" ? unitName : "",

              business: toNumber(numBusiness),
              core2p: toNumber(numCore2P),
              core4p: toNumber(numCore4P),
              coreSfp: toNumber(numCoreSfp),

              extSmall: toNumber(numExtSmall),
              extMedium: toNumber(numExtMedium),
              extLarge: toNumber(numExtLarge),

              virtual: toNumber(numVirtual),

              businessLicense: toNumber(numBusiness) === 0 ? "-" : editBusinessLicense,
              core2pLicense: toNumber(numCore2P) === 0 ? "-" : editCore2pLicense,
              core4pLicense: toNumber(numCore4P) === 0 ? "-" : editCore4pLicense,
              coreSfpLicense: toNumber(numCoreSfp) === 0 ? "-" : editCoreSfpLicense,

              extSmallLicense: toNumber(numExtSmall) === 0 ? "-" : "Lecs Platform Ext. Small",
              extMediumLicense: toNumber(numExtMedium) === 0 ? "-" : "Lecs Platform Ext. Medium",
              extLargeLicense: toNumber(numExtLarge) === 0 ? "-" : "Lecs Platform Ext. Large",

              virtualLicense: toNumber(numVirtual) === 0 ? "-" : virtualTypeLicenses[0],

              virtualHosts: toNumber(numVirtualHosts),
              corporateAmount: toNumber(numCorporateAmount),
              corporateHosts: toNumber(numCorporateHosts),

              accountItemId,
            };

            await API.mondayUpdateDealProtectionItem({ vat: vatKey, formData });
          }
        } catch (e) {
          
          mondaySyncFailed = true;
        }

        if (mondaySyncFailed) {
          updateToastWarning("Customer updated, but Monday sync failed");
        } else {
          updateToastSuccess("updated customer");
        }
      })
      .catch((err) => {
        updateToastError("Error");
        
      });
  };


  // Valori/setter dei campi anagrafici, condivisi da form di creazione e dialog di modifica
  const form = { vat, isItalianVat, name, email, company, location, address, licenses, businessUnit, unitName };
  const formSetters = {
    vat: handleVatChange,
    vatMode: handleVatModeChange,
    name: setName,
    email: setEmail,
    company: setCompany,
    location: setLocation,
    address: setAddress,
    licenses: (v) => setLicenses(sanitizeNumericInput(v)),
    businessUnit: setBusinessUnit,
    unitName: setUnitName,
  };

  // ---------------------------
  // RESET HELPERS for toggles
  // ---------------------------
  const resetFromBlock2 = () => {
    setSelectedTypeLecs2("");
    setQuantity2("");
    setHosts2("");
    setSelectedTypeLicenses2("");
    resetFromBlock3();
  };

  const resetFromBlock3 = () => {
    setSelectedTypeLecs3("");
    setQuantity3("");
    setHosts3("");
    setSelectedTypeLicenses3("");
    resetFromBlock4();
  };

  const resetFromBlock4 = () => {
    setSelectedTypeLecs4("");
    setQuantity4("");
    setHosts4("");
    setSelectedTypeLicenses4("");
    resetFromBlock5();
  };

  const resetFromBlock5 = () => {
    setSelectedTypeLecs5("");
    setQuantity5("");
    setHosts5("");
    setSelectedTypeLicenses5("");
    resetFromBlock6();
  };

  const resetFromBlock6 = () => {
    setSelectedTypeLecs6("");
    setQuantity6("");
    setHosts6("");
    setSelectedTypeLicenses6("");
  };

  // ---------------------------
  // BLOCCHI PRODOTTO (CREATE): configurazione per indice 0..5
  // ---------------------------
  const blocks = [
    {
      type: selectedTypeLecs1, setType: setSelectedTypeLecs1,
      qty: quantity1, setQty: setQuantity1,
      hosts: hosts1, setHosts: setHosts1,
      lic: selectedTypeLicenses1, setLic: setSelectedTypeLicenses1,
      licTypes: typesLicenses1,
      options: typesLecs.filter((t) => !EXT_TYPES.includes(t)),
    },
    {
      type: selectedTypeLecs2, setType: setSelectedTypeLecs2,
      qty: quantity2, setQty: setQuantity2,
      hosts: hosts2, setHosts: setHosts2,
      lic: selectedTypeLicenses2, setLic: setSelectedTypeLicenses2,
      licTypes: typesLicenses2, options: typesLecs2,
    },
    {
      type: selectedTypeLecs3, setType: setSelectedTypeLecs3,
      qty: quantity3, setQty: setQuantity3,
      hosts: hosts3, setHosts: setHosts3,
      lic: selectedTypeLicenses3, setLic: setSelectedTypeLicenses3,
      licTypes: typesLicenses3, options: typesLecs3,
    },
    {
      type: selectedTypeLecs4, setType: setSelectedTypeLecs4,
      qty: quantity4, setQty: setQuantity4,
      hosts: hosts4, setHosts: setHosts4,
      lic: selectedTypeLicenses4, setLic: setSelectedTypeLicenses4,
      licTypes: typesLicenses4, options: typesLecs4,
    },
    {
      type: selectedTypeLecs5, setType: setSelectedTypeLecs5,
      qty: quantity5, setQty: setQuantity5,
      hosts: hosts5, setHosts: setHosts5,
      lic: selectedTypeLicenses5, setLic: setSelectedTypeLicenses5,
      licTypes: typesLicenses5, options: typesLecs5,
    },
    {
      type: selectedTypeLecs6, setType: setSelectedTypeLecs6,
      qty: quantity6, setQty: setQuantity6,
      hosts: hosts6, setHosts: setHosts6,
      lic: selectedTypeLicenses6, setLic: setSelectedTypeLicenses6,
      licTypes: typesLicenses6, options: typesLecs6,
    },
  ];

  const visibilities = [true, visibility2, visibility3, visibility4, visibility5, visibility6];
  const setVisibilities = [null, setVisibility2, setVisibility3, setVisibility4, setVisibility5, setVisibility6];
  const resetFromBlock = [null, null, resetFromBlock2, resetFromBlock3, resetFromBlock4, resetFromBlock5, resetFromBlock6];

  // Il bottone del blocco n apre/chiude il blocco n+1 e azzera tutti i successivi.
  // visibilities/setVisibilities sono indicizzati per n (posizione n = visibilità del blocco n+1),
  // resetFromBlock per numero di blocco.
  const toggleNextBlock = (n) => {
    const next = n + 1;
    setVisibilities[n]((v) => !v);
    resetFromBlock[next]();
    for (let k = next + 1; k <= 6; k++) setVisibilities[k - 1](false);
  };

  // ---------------------------
  // RIGHE EDIT (dialog)
  // ---------------------------
  const platformRows = [
    { label: PLATFORM_MICRO, qty: numBusiness, setQty: setNumBusiness, qtyErr: errors.business, lic: editBusinessLicense, setLic: setEditBusinessLicense, licErr: errors.editBusinessLicense, options: businessTypeLicenses },
    { label: PLATFORM_SMALL, qty: numCore2P, setQty: setNumCore2P, qtyErr: errors.core2p, lic: editCore2pLicense, setLic: setEditCore2pLicense, licErr: errors.editCore2pLicense, options: core2pTypeLicenses },
    { label: PLATFORM_MEDIUM, qty: numCore4P, setQty: setNumCore4P, qtyErr: errors.core4p, lic: editCore4pLicense, setLic: setEditCore4pLicense, licErr: errors.editCore4pLicense, options: core4pTypeLicenses },
    { label: PLATFORM_LARGE, qty: numCoreSfp, setQty: setNumCoreSfp, qtyErr: errors.coreSfp, lic: editCoreSfpLicense, setLic: setEditCoreSfpLicense, licErr: errors.editCoreSfpLicense, options: coreSfpTypeLicenses },
    { label: PLATFORM_EXT_SMALL, qty: numExtSmall, setQty: setNumExtSmall, qtyErr: errors.extSmall, lic: editExtSmallLicense, setLic: setEditExtSmallLicense, licErr: errors.editExtSmallLicense, options: extSmallTypeLicenses },
    { label: PLATFORM_EXT_MEDIUM, qty: numExtMedium, setQty: setNumExtMedium, qtyErr: errors.extMedium, lic: editExtMediumLicense, setLic: setEditExtMediumLicense, licErr: errors.editExtMediumLicense, options: extMediumTypeLicenses },
    { label: PLATFORM_EXT_LARGE, qty: numExtLarge, setQty: setNumExtLarge, qtyErr: errors.extLarge, lic: editExtLargeLicense, setLic: setEditExtLargeLicense, licErr: errors.editExtLargeLicense, options: extLargeTypeLicenses },
  ];

  const hostRows = [
    { label: VIRTUAL_TYPE, qty: numVirtual, setQty: setNumVirtual, qtyErr: errors.virtual, hosts: numVirtualHosts, setHosts: setNumVirtualHosts, hostsErr: errors.virtualHosts },
    { label: CORPORATE_TYPE, qty: numCorporateAmount, setQty: setNumCorporateAmount, qtyErr: errors.corporateAmount, hosts: numCorporateHosts, setHosts: setNumCorporateHosts, hostsErr: errors.corporateHosts },
  ];

  // Stato di scadenza calcolato una volta per riga
  const protectionRows = useMemo(() => {
    const now = dayjs();
    const nameOf = (r) => String(r.companyName || r.name || "").trim();
    return (Array.isArray(rows) ? rows : [])
      .map((r) => {
        const d = r.expireAt && !/NaN/.test(String(r.expireAt)) ? dayjs(r.expireAt) : null;
        const valid = !!d && d.isValid();
        const expired = valid && d.valueOf() < now.valueOf();
        const daysLeft = valid ? Math.ceil(d.diff(now, "day", true)) : null;
        const state = expired ? "expired" : daysLeft !== null && daysLeft <= EXPIRING_DAYS ? "expiring" : "active";
        return { ...r, expired, daysLeft, state };
      })
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base", numeric: true }));
  }, [rows]);

  const stateCounts = useMemo(() => {
    const c = { all: protectionRows.length, active: 0, expiring: 0, expired: 0 };
    protectionRows.forEach((r) => {
      if (!r.expired) c.active += 1;
      if (r.state === "expiring") c.expiring += 1;
      if (r.expired) c.expired += 1;
    });
    return c;
  }, [protectionRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return protectionRows.filter((r) => {
      if (stateFilter === "active" && r.expired) return false;
      if (stateFilter === "expiring" && r.state !== "expiring") return false;
      if (stateFilter === "expired" && !r.expired) return false;
      if (!q) return true;
      return [r.companyName, r.name, r.email, r.vat, r.location, r.address].some((v) =>
        String(v ?? "").toLowerCase().includes(q)
      );
    });
  }, [protectionRows, search, stateFilter]);

  // ---------------------------
  // TABLE COLUMNS
  // ---------------------------
  const muted = (text) => (
    <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>
      {text}
    </Typography>
  );

  const columns = [
    { field: "companyName", headerName: "Company", minWidth: 180, flex: 1.2, renderCell: (p) => p.value || muted("-") },
    { field: "name", headerName: "Contact", minWidth: 150, flex: 1, renderCell: (p) => p.value || muted("-") },
    { field: "vat", headerName: "VAT", width: 140, renderCell: (p) => p.value || muted("-") },
    { field: "location", headerName: "Location/Department", minWidth: 160, flex: 1, renderCell: (p) => p.value || muted("-") },
    { field: "address", headerName: "Address", minWidth: 180, flex: 1, renderCell: (p) => p.value || muted("-") },
    {
      field: "expireAt",
      headerName: "Expires",
      width: 150,
      renderCell: (p) => formatDate(p.value) ?? muted("-"),
    },
    {
      field: "daysLeft",
      headerName: "Days left",
      width: 110,
      renderCell: (p) => {
        if (p.row.expired) {
          return (
            <Typography component="span" sx={{ fontSize: 13, color: palette.error.light }}>
              Expired
            </Typography>
          );
        }
        if (p.value === null || p.value === undefined) return muted("-");
        return (
          <Typography component="span" sx={{ fontSize: 13, color: p.value <= EXPIRING_DAYS ? "#EF9F27" : undefined }}>
            {p.value}
          </Typography>
        );
      },
    },
  ];

  // ---------------------------
  // RENDER
  // ---------------------------
  const panelHeight = { xs: "auto", lg: "calc(100vh - 190px)" };

  const tableToolbar = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        size="small"
        placeholder="Search by company, contact, VAT…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: MUTED }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {STATE_FILTERS.map(({ key, label }) => (
          <Chip
            key={key}
            size="small"
            clickable
            onClick={() => setStateFilter(key)}
            label={`${label} · ${stateCounts[key]}`}
            sx={chipSx(stateFilter === key)}
          />
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Box m="20px">
      <Box mb={2}>
        <Header title="Deal Protection" subtitle="List of customers" />
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Box mb={2}>{tableToolbar}</Box>
          <Box sx={{ height: { xs: 480, lg: "calc(100vh - 250px)" }, minHeight: 480 }}>
            <DataGrid
              sx={{
                ...dataGridStyle,
                "& .MuiDataGrid-row": { cursor: "pointer" },
              }}
              loading={!charged}
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row._id}
              disableRowSelectionOnClick
              onRowClick={(params) => fetchDetails(params.row)}
              onCellKeyDown={(params, event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  fetchDetails(params.row);
                }
              }}
              localeText={{ noRowsLabel: "No customers found" }}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Panel icon={PersonAddIcon} title="New deal protection" sx={{ height: panelHeight, minHeight: 520 }}>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
              <CustomerFields f={form} set={formSetters} errors={errors} />

              <SectionLabel>Products</SectionLabel>
              <Stack spacing={1.5}>
                {blocks.map((cfg, i) => {
                  const n = i + 1;
                  if (!visibilities.slice(0, n).every(Boolean)) return null;
                  return (
                    <ProductBlock
                      key={n}
                      n={n}
                      cfg={cfg}
                      errors={errors}
                      nextOpen={n < 6 ? visibilities[n] : false}
                      onToggle={n < 6 ? () => toggleNextBlock(n) : null}
                    />
                  );
                })}
              </Stack>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                px: 2,
                py: 1.25,
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              <Button color="inherit" onClick={resetForm}>
                Clear
              </Button>
              <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleSubmit}>
                Add
              </Button>
            </Box>
          </Panel>
        </Grid>
      </Grid>

      <ModalFrame
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        subtitle="Customer details"
        title={selectedRow?.companyName}
        actions={
          <>
            <Button onClick={() => setConfirmDeleteOpen(true)} color="error" sx={{ mr: "auto" }}>
              Delete
            </Button>
            <Button onClick={() => setOpenDialog(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={updateSubmit} variant="contained">
              Apply changes
            </Button>
          </>
        }
      >
        {selectedRow ? (
          <>
            <CustomerFields f={form} set={formSetters} errors={errors} />

            <SectionLabel>Appliances</SectionLabel>
            <Stack spacing={1.5}>
              {platformRows.map((r) => (
                <EditRow key={r.label} label={r.label}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <QtyField value={r.qty} onChange={r.setQty} error={r.qtyErr} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4.5 }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="License type"
                      value={r.lic}
                      error={!!r.licErr}
                      helperText={r.licErr}
                      onChange={(e) => r.setLic(e.target.value)}
                    >
                      <MenuItem value="-">None</MenuItem>
                      {r.options.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </EditRow>
              ))}

              {hostRows.map((r) => (
                <EditRow key={r.label} label={r.label}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <QtyField value={r.qty} onChange={r.setQty} error={r.qtyErr} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4.5 }}>
                    <HostsField value={r.hosts} onChange={r.setHosts} error={r.hostsErr} />
                  </Grid>
                </EditRow>
              ))}
            </Stack>
          </>
        ) : (
          <Typography>Loading...</Typography>
        )}
      </ModalFrame>

      <ModalFrame
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        title="Confirm deletion"
        actions={
          <>
            <Button onClick={() => setConfirmDeleteOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={() => {
                deleteSubmit();
                setConfirmDeleteOpen(false);
                setOpenDialog(false);
              }}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography>Are you sure you want to delete this customer? This action is irreversible.</Typography>
      </ModalFrame>
    </Box>
  );
};

export default DealProtection;
